import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsTrendWidgetComponent } from '@enginuity/analytics/organisms/analytics-trend-widget/analytics-trend-widget.component';
import { GlobalLoaderWrapperComponent } from '@enginuity/analytics/organisms/global-loader-wrapper/global-loader-wrapper.component';
import { TableViewComponent } from '@enginuity/core/organisms/table-view/table-view.component';
import { TopCustomerList } from '@services/analytics-services/customers/dashboard-customer-table-header.constant';
import { SubsequentPurchasesList } from '@services/analytics-services/customers/dashboard-subsequent-purchase-table-header.constant';
import { mapDashboardCustomerTableData } from '@services/analytics-services/customers/dashboard-customer.table.mapper';
import { mapSubsequentPurchaseTableData } from '@services/analytics-services/customers/dashboard-subsequent-purchase-table.mapper';
import { TableView } from '@services/ui-services/models';
import { Subject, takeUntil, switchMap, forkJoin } from 'rxjs';
import { DashboardStore } from '../dashboard/dashboard.store';
import { DashboardCustomersStore } from './dashboard-customers.store';

@Component({
  selector: 'app-dashboard-customers',
  imports: [
    AsyncPipe,
    AnalyticsTrendWidgetComponent,
    TableViewComponent,
    GlobalLoaderWrapperComponent,
  ],
  templateUrl: './dashboard-customers.component.html',
  styleUrl: './dashboard-customers.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DashboardCustomersStore],
})
export class DashboardCustomersComponent implements OnInit, OnDestroy {
  // Get signals directly from the store
  protected rangeContext = this.dashboardStore.getRangeContextSignal();

  // Create resources using the new resource pattern
  protected customerAnalyticsResource =
    this.dashboardCustomersStore.createCustomerAnalyticsResource(this.rangeContext);

  public topCustomerLoader$ = this.dashboardCustomersStore.getTopCustomerLoader();
  public topCustomerErrorMessage$ = this.dashboardCustomersStore.getTopCustomerErrorMessage();
  public subsequentPurchasesLoader$ = this.dashboardCustomersStore.getsubsequentPurchasesLoader();
  public subsequentPurchasesErrorMessage$ =
    this.dashboardCustomersStore.getSubsequentPurchasesErrorMessage();

  protected customerTable: TableView = {
    head: TopCustomerList,
    rows: [],
    data: [],
  };

  protected subsequentPurchasesTable: TableView = {
    head: SubsequentPurchasesList,
    rows: [],
    data: [],
  };

  private destroy$ = new Subject<void>();

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly dashboardCustomersStore: DashboardCustomersStore,
    private router: Router
  ) {
    // Subscribe to top customers data and map it for the table
    this.dashboardCustomersStore
      .getTopCustomers()
      .pipe(takeUntil(this.destroy$))
      .subscribe(customers => {
        this.customerTable = {
          ...this.customerTable,
          rows: customers?.map(customer => mapDashboardCustomerTableData(customer)) ?? [],
        };
      });

    // Subscribe to subsequent purchases data and map it for the table
    this.dashboardCustomersStore
      .getSubsequentPurchases()
      .pipe(takeUntil(this.destroy$))
      .subscribe(purchases => {
        this.subsequentPurchasesTable = {
          ...this.subsequentPurchasesTable,
          rows: purchases?.map(purchase => mapSubsequentPurchaseTableData(purchase)) ?? [],
        };
      });
  }

  ngOnInit() {
    this.dashboardStore
      .getRangeContext()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(context => {
          if (context) {
            // Use forkJoin to execute all API calls in parallel
            // switchMap will cancel previous forkJoin if a new context is emitted
            return forkJoin([
              this.dashboardCustomersStore.loadTopCustomer(context),
              this.dashboardCustomersStore.loadSubsequentPurchases(context),
            ]);
          }
          return [];
        })
      )
      .subscribe();
  }

  onActionClick(entry: any) {
    console.log(entry);
    if (entry.action == 'customer-detail') {
      this.router.navigateByUrl(`customers/${entry.value.customerId}/overview`);
    } else if (entry.action == 'user-detail') {
      this.router.navigateByUrl(`customers/${entry.customerId}/overview`);
    }
  }

  onSort(sortEvent: any) {
    this.dashboardCustomersStore.sortTopCustomers(sortEvent);
  }

  onSortSubsequentPurchases(sortEvent: any) {
    this.dashboardCustomersStore.sortSubsequentPurchases(sortEvent);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
